import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { platformApi } from '../../api/platformApi';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Loader2, Plus, Search, Building2, Eye, Power, PowerOff } from 'lucide-react';
import { toast } from 'sonner';

export const PlatformTenants = () => {
    const { token } = useAuth();
    const [tenants, setTenants] = useState([]);
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');

    // Create Modal
    const [createOpen, setCreateOpen] = useState(false);
    const [createForm, setCreateForm] = useState({
        nombre: '',
        tenant_type: 'LAW_FIRM',
        activo: true,
        admin_email: '',
        admin_nombre: '',
        dominio: ''
    });

    // Detail Modal
    const [selectedTenant, setSelectedTenant] = useState(null);
    const [detailOpen, setDetailOpen] = useState(false);

    // Assign Plan
    const [assignPlanId, setAssignPlanId] = useState('');

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            const [tData, pData] = await Promise.all([
                platformApi.getTenants(token, true),
                platformApi.getPlans(token)
            ]);
            setTenants(tData);
            setPlans(pData);
        } catch (error) {
            toast.error("Error cargando tenants");
        } finally {
            setLoading(false);
        }
    };

    const handleCreate = async () => {
        if (!createForm.nombre) {
            toast.error("El nombre es obligatorio");
            return;
        }

        try {
            await platformApi.createTenant(token, createForm);
            toast.success("Tenant creado");
            setCreateOpen(false);
            setCreateForm({
                nombre: '',
                tenant_type: 'LAW_FIRM',
                activo: true,
                admin_email: '',
                admin_nombre: '',
                dominio: ''
            });
            loadData();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Error al crear");
        }
    };

    const toggleStatus = async (t) => {
        if (!window.confirm(`¿${t.activo ? 'Desactivar' : 'Activar'} tenant?`)) return;
        try {
            await platformApi.toggleTenantStatus(token, t.id, !t.activo);
            toast.success("Estado actualizado");
            loadData();
        } catch (e) { toast.error("Error"); }
    };

    const handleAssignPlan = async () => {
        try {
            await platformApi.assignPlan(token, selectedTenant.id, assignPlanId);
            toast.success("Plan asignado");
            loadData();
            setDetailOpen(false);
        } catch (e) { toast.error("Error asignando plan"); }
    };

    const safeLower = (text) => text ? text.toString().toLowerCase() : '';
    const filtered = tenants.filter(t => safeLower(t.nombre).includes(safeLower(searchQuery)));

    const getTenantTypeLabel = (type) => {
        switch (type) {
            case 'LAW_FIRM': return 'Despacho';
            case 'COMPANY': return 'Empresa';
            case 'END_USER': return 'Usuario Final';
            case 'INTERNAL_FIRM': return 'Interno';
            default: return type || 'N/A';
        }
    };

    const getTenantTypeBadge = (type) => {
        switch (type) {
            case 'LAW_FIRM': return 'default';
            case 'COMPANY': return 'secondary';
            case 'END_USER': return 'outline';
            case 'INTERNAL_FIRM': return 'destructive';
            default: return 'outline';
        }
    };

    if (loading) return <Loader2 className="animate-spin" />;

    return (
        <div className="space-y-4">
            <div className="flex flex-col sm:flex-row justify-between gap-4">
                <div className="relative w-full sm:w-64">
                    <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input placeholder="Buscar tenant..." className="pl-8" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
                </div>
                <Button onClick={() => setCreateOpen(true)} className="w-full sm:w-auto"><Plus className="mr-2 h-4 w-4" /> Nuevo Tenant</Button>
            </div>

            {/* Desktop Table */}
            <div className="hidden md:block border rounded-md bg-white">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>Nombre</TableHead>
                            <TableHead>Tipo</TableHead>
                            <TableHead>Plan</TableHead>
                            <TableHead>Estado</TableHead>
                            <TableHead className="text-right">Acciones</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {filtered.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={5} className="text-center py-6 text-gray-500">
                                    No se encontraron tenants
                                </TableCell>
                            </TableRow>
                        ) : (
                            filtered.map(t => (
                                <TableRow key={t.id}>
                                    <TableCell className="font-medium">{t.nombre || <span className="text-gray-400 italic">Sin Nombre</span>}</TableCell>
                                    <TableCell>
                                        <Badge variant={getTenantTypeBadge(t.tenant_type)}>
                                            {getTenantTypeLabel(t.tenant_type)}
                                        </Badge>
                                    </TableCell>
                                    <TableCell>{plans.find(p => p.id === t.plan_id)?.name || <span className="text-gray-400">Sin plan</span>}</TableCell>
                                    <TableCell>
                                        <Badge variant={t.activo ? "success" : "destructive"} className={t.activo ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}>
                                            {t.activo ? "Activo" : "Inactivo"}
                                        </Badge>
                                    </TableCell>
                                    <TableCell className="text-right space-x-1">
                                        <Button variant="ghost" size="sm" onClick={() => { setSelectedTenant(t); setDetailOpen(true); setAssignPlanId(t.plan_id || ''); }}>
                                            <Eye className="h-4 w-4" />
                                        </Button>
                                        <Button variant="ghost" size="sm" onClick={() => toggleStatus(t)}>
                                            {t.activo ? <PowerOff className="h-4 w-4 text-red-500" /> : <Power className="h-4 w-4 text-green-500" />}
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            )))}
                    </TableBody>
                </Table>
            </div>

            {/* Mobile Cards */}
            <div className="md:hidden space-y-4">
                {filtered.map(t => (
                    <div
                        key={t.id}
                        className="p-4 bg-white border rounded-lg shadow-sm space-y-3"
                    >
                        <div className="flex justify-between items-start">
                            <div>
                                <h3 className="font-bold text-gray-900">{t.nombre || 'Sin nombre'}</h3>
                                <div className="mt-1">
                                    <Badge variant={getTenantTypeBadge(t.tenant_type)}>
                                        {getTenantTypeLabel(t.tenant_type)}
                                    </Badge>
                                </div>
                            </div>
                            <div className="flex gap-1">
                                <Button variant="ghost" size="sm" onClick={() => { setSelectedTenant(t); setDetailOpen(true); setAssignPlanId(t.plan_id || ''); }}>
                                    <Eye className="h-4 w-4" />
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => toggleStatus(t)}>
                                    {t.activo ? <PowerOff className="h-4 w-4 text-red-500" /> : <Power className="h-4 w-4 text-green-500" />}
                                </Button>
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-2 pt-2 border-t text-sm">
                            <div>
                                <span className="text-gray-500 block text-xs">Plan</span>
                                <span>{plans.find(p => p.id === t.plan_id)?.name || 'Sin plan'}</span>
                            </div>
                            <div>
                                <span className="text-gray-500 block text-xs">Estado</span>
                                <Badge variant={t.activo ? "success" : "destructive"} className={t.activo ? "text-[10px] bg-green-50 text-green-700" : "text-[10px] bg-red-50 text-red-700 hover:bg-red-50"}>
                                    {t.activo ? "Activo" : "Inactivo"}
                                </Badge>
                            </div>
                        </div>
                    </div>
                ))}
                {filtered.length === 0 && (
                    <div className="text-center py-10 bg-gray-50 rounded-lg border border-dashed">
                        <p className="text-gray-500">No se encontraron tenants</p>
                    </div>
                )}
            </div>

            {/* Create Tenant Modal */}
            <Dialog open={createOpen} onOpenChange={setCreateOpen}>
                <DialogContent>
                    <DialogHeader><DialogTitle>Nuevo Tenant</DialogTitle></DialogHeader>
                    <div className="space-y-4 py-2">
                        <div className="space-y-2">
                            <Label>Nombre *</Label>
                            <Input placeholder="Ej. Bufete Jurídico S.C." value={createForm.nombre} onChange={e => setCreateForm({ ...createForm, nombre: e.target.value })} />
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Tipo</Label>
                                <Select value={createForm.tenant_type} onValueChange={v => setCreateForm({ ...createForm, tenant_type: v })}>
                                    <SelectTrigger><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="LAW_FIRM">Despacho (Law Firm)</SelectItem>
                                        <SelectItem value="COMPANY">Empresa (Company)</SelectItem>
                                        <SelectItem value="END_USER">Usuario Final (B2C)</SelectItem>
                                        <SelectItem value="INTERNAL_FIRM">Interno (Admin)</SelectItem>
                                    </SelectContent>
                                </Select>
                                <p className="text-[10px] text-muted-foreground">
                                    {createForm.tenant_type === 'END_USER' ? 'Usuario individual con acceso limitado.' :
                                        createForm.tenant_type === 'INTERNAL_FIRM' ? 'Uso interno de la plataforma.' :
                                            'Organización estándar con múltiples usuarios.'}
                                </p>
                            </div>
                            <div className="space-y-2">
                                <Label>Dominio (Slug)</Label>
                                <Input placeholder="Opcional" value={createForm.dominio} onChange={e => setCreateForm({ ...createForm, dominio: e.target.value })} />
                            </div>
                        </div>

                        <div className="border-t pt-4 mt-2">
                            <Label className="text-base font-semibold">Administrador Inicial (Opcional)</Label>
                            <p className="text-xs text-muted-foreground mb-3">Se creará un usuario admin automáticamente.</p>

                            <div className="space-y-3">
                                <Input placeholder="Email del Admin" value={createForm.admin_email} onChange={e => setCreateForm({ ...createForm, admin_email: e.target.value })} />
                                <Input placeholder="Nombre Completo" value={createForm.admin_nombre} onChange={e => setCreateForm({ ...createForm, admin_nombre: e.target.value })} />
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button onClick={handleCreate}>Crear Tenant</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Detail Modal */}
            <Dialog open={detailOpen} onOpenChange={setDetailOpen} maxWidth="max-w-md">
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Building2 className="h-5 w-5" /> {selectedTenant?.nombre}
                        </DialogTitle>
                    </DialogHeader>

                    {selectedTenant && (
                        <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-4 text-sm">
                                <div>
                                    <span className="font-semibold block">ID:</span>
                                    <span className="font-mono text-xs">{selectedTenant.id}</span>
                                </div>
                                <div>
                                    <span className="font-semibold block">Slug:</span>
                                    <span>{selectedTenant.slug}</span>
                                </div>
                                <div>
                                    <span className="font-semibold block">Tipo:</span>
                                    <Badge variant={getTenantTypeBadge(selectedTenant.tenant_type)}>
                                        {getTenantTypeLabel(selectedTenant.tenant_type)}
                                    </Badge>
                                    <span className="text-xs text-muted-foreground ml-2">({selectedTenant.tenant_type})</span>
                                </div>
                                <div>
                                    <span className="font-semibold block">Estado:</span>
                                    <Badge variant={selectedTenant.activo ? "success" : "destructive"}>
                                        {selectedTenant.activo ? "Activo" : "Inactivo"}
                                    </Badge>
                                </div>
                            </div>

                            <div className="border-t pt-4">
                                <Label className="mb-2 block">Asignar Plan</Label>
                                <div className="flex gap-2">
                                    <Select value={assignPlanId} onValueChange={setAssignPlanId}>
                                        <SelectTrigger className="w-full">
                                            <SelectValue placeholder="Seleccionar plan" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="none">Sin plan</SelectItem>
                                            {plans.map(p => <SelectItem key={p.id} value={p.id}>{p.name} ({p.code})</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                    <Button onClick={handleAssignPlan}>Guardar</Button>
                                </div>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
};
